import React, { useState, useRef, useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useSentinelChat } from './SentinelChatProvider'

const MAX_CHARS = 5000

/** Question input, docked at the bottom of the chat. Gold = the one primary action. */
export default function Composer() {
  const { t } = useTranslation()
  const { ask, loading, turns } = useSentinelChat()
  const [value, setValue] = useState('')
  const textareaRef = useRef(null)
  const over = value.length > MAX_CHARS
  const nearLimit = value.length > MAX_CHARS - 1000

  // Grow the textarea to fit its content — starts at one line, expands up to
  // 10 lines, then scrolls.
  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    const cs = window.getComputedStyle(el)
    const lineHeight = parseFloat(cs.lineHeight) || (parseFloat(cs.fontSize) || 15) * 1.5
    const padding = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0)
    const maxHeight = lineHeight * 10 + padding
    el.style.height = 'auto' // reset so scrollHeight reflects the true content height
    const next = Math.min(el.scrollHeight, maxHeight)
    el.style.height = `${next}px`
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
  }, [value])

  const followUps = t('Sentinel.V2.followUp.items', { returnObjects: true })
  const hasResult = turns.some((turn) => turn.role === 'assistant' && turn.status === 'result')
  const showFollowUps = hasResult && !loading && !value.trim() && Array.isArray(followUps)

  const submit = () => {
    const v = value.trim()
    if (!v || loading || over) return
    ask(v)
    setValue('')
  }

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  return (
    <div className="fd-composer" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '12px', padding: '12px 14px' }}>
      {showFollowUps && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="fd-eyebrow" style={{ marginRight: '4px' }}>{t('Sentinel.V2.followUp.label')}</span>
          {followUps.map((q) => (
            <button key={q} type="button" className="fd-chip" onClick={() => ask(q)}>
              {q}
            </button>
          ))}
        </div>
      )}
      <label htmlFor="sentinel-composer" className="fd-visually-hidden">{t('Sentinel.V2.composer.label')}</label>
      <textarea
        id="sentinel-composer"
        ref={textareaRef}
        rows={1}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={t('Sentinel.V2.composer.placeholder')}
      />
      <div className="flex items-center justify-between gap-3">
        {nearLimit ? (
          <span className={`fd-mono ${over ? 'fd-hint fd-hint--error' : 'fd-body-sm'}`} style={over ? undefined : { color: 'var(--text-tertiary)' }}>
            {over && <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" style={{ marginRight: 6 }} />}
            {value.length.toLocaleString()} / {MAX_CHARS.toLocaleString()}
          </span>
        ) : (
          <span className="fd-body-sm" style={{ color: 'var(--text-tertiary)' }}>
            {t('Sentinel.V2.composer.hint')}
          </span>
        )}
        <button className="fd-btn fd-btn--gold" onClick={submit} disabled={loading || !value.trim() || over}>
          {loading ? t('Sentinel.V2.composer.asking') : t('Sentinel.V2.composer.ask')}
        </button>
      </div>
    </div>
  )
}
